/**
 * Begrüßungsseite für neue Händler (Jul-2026, User-Wunsch).
 *
 * Nach der Registrierung landet der Händler hier (statt im Dashboard):
 *  1. Funktionsübersicht — was twowheels.info alles kann.
 *  2. Status-Checkliste: E-Mail bestätigen → Freigabe durch das Team.
 *  3. Offene Zustimmungen (Spielregeln etc.), sofern noch nicht erteilt.
 * Eingaben & Einstellungen sind bis zur Freigabe gesperrt — Umsehen erlaubt.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    CheckCircle2, Circle, Clock, MailCheck, ShieldCheck, ScrollText, Loader2,
    Store, Camera, Wrench, Package, FileText, Calculator, Truck, Inbox,
    Landmark, RotateCcw, Mail, BarChart3, Users, Sparkles, ArrowRight, Search,
    Smartphone,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const FEATURES = [
    { icon: Store, t: "Marktplatz & Inserate", d: "Motorräder & Fahrräder gewerblich verkaufen — B2B & B2C, Auktionen, Preisvorschläge." },
    { icon: Camera, t: "KI-Bilddatenbank", d: "Fahrzeugbilder automatisch freistellen, mit Firmen-Hintergrund versehen und wiederverwenden." },
    { icon: Wrench, t: "Werkstatt", d: "Aufträge, Kalender, Schnell-Erfassung per Scan, Kundenverwaltung und Bewertungen." },
    { icon: Package, t: "Warenwirtschaft", d: "Artikel, Barcode-Scan, Lieferschein-Scan, Bestellwesen und eigener Onlineshop." },
    { icon: FileText, t: "Rechnungen", d: "Rechnungen, Direktverkauf, GoBD-konforme Nummernkreise und PDF-Versand." },
    { icon: Calculator, t: "Buchhaltung", d: "Kreditoren, Zahlungsimport, EC-Belege mit KI, Provisionen, Lohn & DATEV-Export." },
    { icon: Truck, t: "Disposition", d: "Fahrzeug-Dispo, Transportplanung, Inventuren, Papier-Verwaltung, Rote Nummer & Fahrtenbuch." },
    { icon: Inbox, t: "Leads & KI-Assistent", d: "Börsen-Anfragen automatisch erfassen, Lead-Scoring, Anrufliste und KI-Vorschläge." },
    { icon: Landmark, t: "Finanzierungs-Ausläufe", d: "Bank-Listen importieren, Anschlussgeschäft rechtzeitig ansprechen, Wiedervorlagen." },
    { icon: RotateCcw, t: "Leasingrückgabe", d: "Rückgabe oder Übernahme rechnen, Kunden-Landingpages, Termine & digitale Signatur." },
    { icon: Mail, t: "E-Mail-Center", d: "Kunden-Mails im eigenen Markendesign, Vorlagen, Freigaben und Standort-Postfach." },
    { icon: BarChart3, t: "Auswertungen", d: "Umsätze, Margen, Verkäufer-Statistiken und tägliche Kennzahlen auf einen Blick." },
    { icon: Users, t: "Mitarbeiter & Aufgaben", d: "Rollen & Rechte, Aufgaben-Delegation, Zeiterfassung und Provisionsabrechnung." },
    { icon: Sparkles, t: "KI überall", d: "Fahrzeugschein-Scan, Ausstattungs-Erkennung, Textvorschläge und smarte Hinweise." },
];

export default function WelcomePage() {
    const { user: authUser, loading } = useAuth();
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [busy, setBusy] = useState(false);
    const [checks, setChecks] = useState({ agb: false, datenschutz: false, spielregeln: false });
    const [verifyUrl, setVerifyUrl] = useState("");
    // Handy-Verifizierung (Jul-2026, User-Wunsch) — SMS derzeit GEMOCKT (Dev-Code)
    const [codeSent, setCodeSent] = useState(false);
    const [smsCode, setSmsCode] = useState("");
    const [devCode, setDevCode] = useState("");

    const sendPhoneCode = async () => {
        setBusy(true);
        try {
            const r = await api.post("/auth/phone/send-code");
            setCodeSent(true);
            if (r.data?.dev_code) setDevCode(r.data.dev_code);
            toast.success("Code wurde per SMS gesendet.");
        } catch (e) { toast.error(e?.response?.data?.detail || "Code-Versand fehlgeschlagen"); }
        setBusy(false);
    };
    const verifyPhoneCode = async () => {
        setBusy(true);
        try {
            await api.post("/auth/phone/verify-code", { code: smsCode.trim() });
            toast.success("Handynummer bestätigt!");
            setCodeSent(false); setSmsCode(""); setDevCode("");
            await loadMe();
        } catch (e) { toast.error(e?.response?.data?.detail || "Bestätigung fehlgeschlagen"); }
        setBusy(false);
    };

    const loadMe = () => api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    useEffect(() => {
        if (!loading && !authUser) { navigate("/login"); return; }
        if (authUser) loadMe();
    }, [authUser, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading || !me) return <div className="p-12 text-center text-gray-500">Lädt…</div>;

    const emailOk = me.email_verified === true;
    const approved = me.verification_status === "approved" || me.verification_status === "none" || !me.verification_status;
    const rejected = me.verification_status === "rejected";
    const missing = [
        !me.agb_accepted_at && "agb",
        !me.datenschutz_accepted_at && "datenschutz",
        !me.spielregeln_accepted_at && "spielregeln",
    ].filter(Boolean);
    const consentsOk = missing.length === 0;
    const LABELS = { agb: "AGB", datenschutz: "Datenschutzerklärung", spielregeln: "Spielregeln der Plattform" };
    const LINKS = { agb: "/legal/agb", datenschutz: "/legal/datenschutz", spielregeln: "/legal/compliance" };

    const resend = async () => {
        setBusy(true);
        try {
            const r = await api.post("/auth/resend-verification");
            if (r.data?.verify_url) setVerifyUrl(r.data.verify_url);
            toast.success("Bestätigungsmail wurde erneut versendet.");
        } catch (e) { toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen"); }
        setBusy(false);
    };

    const acceptConsents = async () => {
        const body = Object.fromEntries(missing.filter((k) => checks[k]).map((k) => [k, true]));
        if (!Object.keys(body).length) { toast.error("Bitte zuerst ankreuzen."); return; }
        setBusy(true);
        try {
            await api.post("/users/me/accept-terms", body);
            toast.success("Zustimmung gespeichert — vielen Dank!");
            await loadMe();
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
        setBusy(false);
    };

    const Step = ({ done, pending, icon: Icon, title, children }) => (
        <div className="flex items-start gap-3 border border-gray-200 rounded-sm bg-white p-4">
            {done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                : pending ? <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                : <Circle className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" />}
            <div className="min-w-0 flex-1">
                <div className="font-bold text-sm text-[#111827] flex items-center gap-1.5"><Icon className="w-4 h-4 text-gray-400" /> {title}</div>
                <div className="text-xs text-gray-600 mt-1">{children}</div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50" data-testid="welcome-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
                    <div className="text-xs uppercase tracking-wider text-[#FFCC00] font-bold">Willkommen bei twowheels.info</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-2" data-testid="welcome-title">
                        Schön, dass Sie da sind{me.contact_first_name || me.first_name ? `, ${me.contact_first_name || me.first_name}` : ""}!
                    </h1>
                    <p className="text-sm text-gray-300 mt-3 max-w-2xl">
                        Ihr Händlerkonto ist angelegt. Noch zwei kleine Schritte, dann schalten wir Sie frei.
                        Solange dürfen Sie sich gern schon <b className="text-white">umsehen</b> — Eingaben und Einstellungen
                        sind bis zur Freigabe gesperrt.
                    </p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-8">
                {/* Status-Checkliste */}
                <section data-testid="welcome-checklist">
                    <h2 className="text-base md:text-lg font-black text-[#111827] mb-3">So geht es weiter</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Step done={emailOk} pending={!emailOk} icon={MailCheck} title="1. E-Mail bestätigen">
                            {emailOk ? "Ihre E-Mail-Adresse ist bestätigt." : (
                                <>
                                    Bitte klicken Sie auf den Link in unserer Willkommens-Mail.{" "}
                                    <button onClick={resend} disabled={busy} className="font-bold text-blue-700 hover:underline disabled:opacity-50" data-testid="welcome-resend-btn">
                                        Bestätigungsmail erneut senden
                                    </button>
                                    {verifyUrl && (
                                        <div className="mt-1"><a href={verifyUrl} className="font-bold text-blue-700 underline" data-testid="welcome-verify-link">Jetzt bestätigen</a></div>
                                    )}
                                </>
                            )}
                        </Step>
                        <Step done={me.phone_verified === true} pending={me.phone_verified !== true} icon={Smartphone} title="2. Handynummer bestätigen">
                            {me.phone_verified === true ? "Ihre Handynummer ist bestätigt." :
                                (me.phone_mobile || me.phone) ? (
                                <>
                                    Wir senden einen Code an <b>{me.phone_mobile || me.phone}</b>.{" "}
                                    <button onClick={sendPhoneCode} disabled={busy} className="font-bold text-blue-700 hover:underline disabled:opacity-50" data-testid="welcome-phone-send-btn">
                                        {codeSent ? "Code erneut senden" : "Code senden"}
                                    </button>
                                    {codeSent && (
                                        <div className="mt-1.5 flex items-center gap-1.5">
                                            <input value={smsCode} onChange={(e) => setSmsCode(e.target.value)} maxLength={6}
                                                placeholder="6-stelliger Code" inputMode="numeric"
                                                className="w-[110px] text-xs rounded-sm border border-gray-300 px-2 py-1.5 focus:border-[#111827] focus:outline-none"
                                                data-testid="welcome-phone-code-input" />
                                            <button onClick={verifyPhoneCode} disabled={busy || smsCode.trim().length !== 6}
                                                className="text-xs font-bold bg-[#111827] text-white rounded-sm px-2.5 py-1.5 disabled:opacity-50"
                                                data-testid="welcome-phone-verify-btn">Bestätigen</button>
                                        </div>
                                    )}
                                    {devCode && <div className="mt-1 text-[11px] text-gray-400" data-testid="welcome-phone-dev-code">Test-Modus (SMS simuliert): Code {devCode}</div>}
                                </>
                            ) : "Keine Handynummer hinterlegt — unser Team ergänzt sie bei der Freigabe mit Ihnen."}
                        </Step>
                        <Step done={approved && !rejected} pending={!approved && !rejected} icon={ShieldCheck} title="3. Freigabe durch unser Team">
                            {rejected ? "Ihre Freigabe wurde abgelehnt — bitte kontaktieren Sie uns." :
                                approved ? "Ihr Konto ist freigegeben." :
                                "Wir prüfen Ihre Angaben und melden uns zeitnah. Danach können Sie loslegen."}
                        </Step>
                        <Step done={consentsOk} pending={!consentsOk} icon={ScrollText} title="4. Zustimmungen">
                            {consentsOk ? "Alle Zustimmungen liegen vor." : "Bitte stimmen Sie den fehlenden Regelwerken unten zu."}
                        </Step>
                    </div>
                </section>

                {/* Offene Zustimmungen */}
                {!consentsOk && (
                    <section className="border-2 border-[#FFCC00] rounded-sm bg-white p-5" data-testid="welcome-consents">
                        <h2 className="text-base md:text-lg font-black text-[#111827] mb-2">Noch ausstehende Zustimmungen</h2>
                        <div className="space-y-2">
                            {missing.map((k) => (
                                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`welcome-consent-${k}`}>
                                    <input type="checkbox" checked={!!checks[k]}
                                        onChange={(e) => setChecks((p) => ({ ...p, [k]: e.target.checked }))}
                                        className="w-4 h-4 accent-[#111827]" />
                                    <span>Ich habe die <a href={LINKS[k]} target="_blank" rel="noreferrer" className="font-bold text-blue-700 underline">{LABELS[k]}</a> gelesen und stimme zu.</span>
                                </label>
                            ))}
                        </div>
                        <button onClick={acceptConsents} disabled={busy || !missing.some((k) => checks[k])}
                            className="mt-4 inline-flex items-center gap-2 bg-[#111827] text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-black disabled:opacity-50"
                            data-testid="welcome-consents-submit">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Zustimmen
                        </button>
                    </section>
                )}

                {/* Funktionsübersicht */}
                <section data-testid="welcome-features">
                    <h2 className="text-base md:text-lg font-black text-[#111827] mb-1">Das alles kann twowheels.info für Sie tun</h2>
                    <p className="text-xs text-gray-500 mb-4">Ihre komplette Händler-Plattform — vom Inserat bis zur Buchhaltung.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {FEATURES.map((f) => (
                            <div key={f.t} className="border border-gray-200 rounded-sm bg-white p-4 hover:border-[#111827] transition-colors">
                                <div className="flex items-center gap-2 font-bold text-sm text-[#111827]">
                                    <f.icon className="w-4 h-4 text-[#b58900]" /> {f.t}
                                </div>
                                <p className="text-xs text-gray-600 mt-1.5">{f.d}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Aktionen */}
                <section className="flex flex-wrap gap-3 pb-8" data-testid="welcome-actions">
                    <button onClick={() => navigate("/search")}
                        className="inline-flex items-center gap-2 border border-gray-300 bg-white text-[#111827] rounded-sm px-4 py-2.5 text-sm font-bold hover:border-[#111827]"
                        data-testid="welcome-browse-btn">
                        <Search className="w-4 h-4" /> Marktplatz ansehen
                    </button>
                    {emailOk && approved && consentsOk && (
                        <button onClick={() => navigate("/dashboard")}
                            className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] rounded-sm px-4 py-2.5 text-sm font-bold hover:brightness-95"
                            data-testid="welcome-dashboard-btn">
                            Zum Dashboard <ArrowRight className="w-4 h-4" />
                        </button>
                    )}
                </section>
            </div>
        </div>
    );
}
